import { Button } from '@mantine/core';
import { Edit3 } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlag } from '../../../../stores/featureFlags';

interface BuildContentButtonProps {
  cohortId: string;
  sessionNumber: number;
  /**
   * Representative session id for this session number when groups exist. When
   * provided, the button opens the existing sessionId-addressed editor (full
   * feature set incl. slide deck + "Start from existing"). When absent (no
   * groups yet), it opens the by-session-number editor. Both edit the same
   * (cohort, group_coaching, session_number) scope, so the content is identical.
   */
  sessionId?: string | null;
  /**
   * The cohort's group-coaching per-modality live-session-delivery setting — the
   * second switch of e1.3l's two-switch gate (AC2). The global
   * live_session_delivery flag is the first switch, checked here.
   */
  modalityLiveSessionDeliveryEnabled: boolean;
  size?: 'xs' | 'sm' | 'md';
}

/**
 * MED-1086 (E1.3m / AC3, AC12) — session-number-level "Build Content" entry
 * point. Styled as an outlined (default) button with a leading edit icon so it
 * reads as the action you "do" (build content), visually distinct from the
 * filled topic pill (the attribute you "set"). Placed rightmost of the topic
 * affordance per the Visual Direction.
 *
 * AC2: reuses e1.3l's exact two-switch gate — global live_session_delivery flag
 * AND the cohort's group-coaching modality live-session-delivery setting. Fails
 * closed (renders nothing) when either is off.
 */
export function BuildContentButton({
  cohortId,
  sessionNumber,
  sessionId,
  modalityLiveSessionDeliveryEnabled,
  size = 'xs',
}: BuildContentButtonProps) {
  // Hook runs unconditionally; the gate below decides rendering.
  const liveSessionDeliveryFlag = useFeatureFlag('live_session_delivery');
  const navigate = useNavigate();

  if (!liveSessionDeliveryFlag || !modalityLiveSessionDeliveryEnabled) return null;

  const target = sessionId
    ? `/cohorts/${cohortId}/sessions/${sessionId}/content`
    : `/cohorts/${cohortId}/sessions/by-number/${sessionNumber}/content`;

  return (
    <Button
      variant="default"
      size={size}
      leftSection={<Edit3 size={14} />}
      onClick={(e) => {
        e.stopPropagation();
        navigate(target);
      }}
      data-testid={`build-content-button-${sessionNumber}`}
    >
      Build Content
    </Button>
  );
}

export default BuildContentButton;
